import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { Dialog, DialogModule } from 'primeng/dialog';
import { TranslocoPipe } from '@jsverse/transloco';
import { ModalService } from './modal.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [DialogModule, TranslocoPipe],
    encapsulation: ViewEncapsulation.None
})
export class Modal {

    public readonly modalService = inject(ModalService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly sanitizer = inject(DomSanitizer);

    public title: string = '';
    public content: SafeHtml = '';
    public type: string = 'message';
    public size: string;

    public readonly visible = this.modalService.visible;
    public readonly dismissableMask = this.modalService.dismissableMask;
    public readonly closeOnEscape = this.modalService.closeOnEscape;
    // Plain getter (re-read every change detection), NOT a computed(): type/size
    // are plain fields set per-invocation, so a computed() would memoise the
    // bootstrap value ('message'/'md') and never reflect the actual modal —
    // leaving every dialog as modal-md with no type class.
    public get dialogStyleClass(): string {
        return `app-modal modal-${this.size || 'md'} modal-type-${this.type}`;
    }

    // PrimeNG locks body scroll (body.p-overflow-hidden) for every modal dialog —
    // `blockScroll` is ignored when `modal` is set — and strips the class again on
    // close even if another modal (details) still needs it. Toggling the class over
    // an open details overlay shows/hides the page scrollbar and shifts the overlay
    // sideways. These messages never own the lock: leave it as it was before they
    // opened, dropping it on close only if no other modal is left to hold it.
    private bodyWasScrollLocked = false;

    @ViewChild(Dialog)
    private set dialog(dialog: Dialog | undefined) {
        if (!dialog) return;
        const enableModality = dialog.enableModality.bind(dialog);
        dialog.enableModality = () => {
            this.bodyWasScrollLocked = document.body.classList.contains('p-overflow-hidden');
            enableModality();
            this.restoreBodyScrollLock();
        };
    }

    constructor() {
        this.modalService.modalInvoked.subscribe((configuration) => {
            this.title = configuration.title;
            this.content = this.sanitizer.bypassSecurityTrustHtml(configuration.content);
            this.type = configuration.type;
            this.size = configuration.size;
            this.changeDetector.detectChanges();
        });
    }

    public emitModalAction(action: string) {
        this.modalService.emitModalAction(action);
        this.modalService.hide();
    }

    public dismiss() {
        this.modalService.hide();
    }

    public goToSubscriptions() {
        this.modalService.goToSubscriptions();
    }

    public onDialogHide() {
        // Fired when PrimeNG closes the dialog (Esc, mask click, close button) —
        // synchronously after it removed body.p-overflow-hidden, so no repaint between.
        this.restoreBodyScrollLock();
        if (this.modalService.isOpen()) {
            this.modalService.hide();
        }
        // Reset while hidden so the next open starts at the top without a flash.
        this.resetScroll();
    }

    public onDialogShow() {
        // Belt-and-suspenders for close paths that don't go through onHide.
        this.resetScroll();
    }

    private restoreBodyScrollLock() {
        const otherScrollBlocker = document.querySelector('[data-p-scrollblocker-active="true"]:not(.app-modal-mask)');
        document.body.classList.toggle('p-overflow-hidden', this.bodyWasScrollLocked && !!otherScrollBlocker);
    }

    // The single app-level <modal> reuses one p-dialog, so .p-dialog-content keeps
    // its scrollTop between opens. Reset it so every modal starts scrolled to top.
    private resetScroll() {
        const content = document.querySelector('.app-modal .p-dialog-content');
        if (content) content.scrollTop = 0;
    }
}
